import React, { useState, useRef, useEffect } from "react";
import "../components/CodingTest/online.css";
import api from "@/components/customAxios/Axios";
import { useNavigate, useParams } from "react-router-dom";
import getAssessmentSheet from "@/api/assessmentSheet";
import { useToast } from "../components/toast";
import { useLoader } from "@/context/loaderContext";
import AssessmentPagesFooter from "@/components/AssessmentPagesFooter";
import AssessmentSideBar from "@/components/AssessmentSideBar";

export function isMobile() {
	return /Mobi|Android/i.test(navigator.userAgent);
}

const AssessmentRequirements: React.FC = () => {
	const { setLoading } = useLoader();
	const toast = useToast();
	const [userPhoto, setUserPhoto] = useState<string | null>(null);
	const [isMicrophoneAccessible, setIsMicrophoneAccessible] =
		useState<boolean>(false);
	const [isFullScreenAccessible, setIsFullScreenAccessible] =
		useState<boolean>(false);
	useState<boolean>(true);
	const [isPhone, setIsPhone] = useState<boolean>(false);
	const [isCameraAccessible, setIsCameraAccessible] = useState<boolean>(false);
	const [isBrowserAccessible, setIsBrowserAccessible] = useState<boolean>(true);
	const [hasTakenSelfie, setHasTakenSelfie] = useState<boolean>(false);
	// const [isAssessmentLoaded, setIsAssessmentLoaded] = useState<boolean>(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const navigate = useNavigate();
	const { token } = useParams<{ token: string }>();
	const enableFullScreen = () => {
		const element = document.documentElement;
		if (!document.fullscreenElement) {
			// Request fullscreen
			element
				.requestFullscreen()
				.catch((err) => console.error("Error requesting fullscreen:", err));
			setIsFullScreenAccessible(true);
		} else {
			// Exit fullscreen
			document
				.exitFullscreen()
				.catch((err) => console.error("Error exiting fullscreen:", err));
			setIsFullScreenAccessible(false);
		}
	};

	// Handle fullscreen change event to update state
	const handleFullscreenChange = () => {
		if (document.fullscreenElement) {
			setIsFullScreenAccessible(true);
		} else {
			setIsFullScreenAccessible(false);
		}
	};

	useEffect(() => {
		localStorage.clear();
		sessionStorage.clear();
	}, []);
	// Add event listener when component mounts
	useEffect(() => {
		document.addEventListener("fullscreenchange", handleFullscreenChange);

		// Clean up the event listener when component unmounts
		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
		};
	}, []);
	useEffect(() => {
		sessionStorage.clear();
		setIsPhone(isMobile());

		const checkMediaAccess = async () => {
			if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
				setIsBrowserAccessible(false);
			}

			try {
				const cameraStream = await navigator.mediaDevices.getUserMedia({
					video: true,
				});
				setIsCameraAccessible(true);
				if (videoRef.current) {
					videoRef.current.srcObject = cameraStream;
				}
			} catch (error) {
				setIsCameraAccessible(false);
			}

			try {
				const microphoneStream = await navigator.mediaDevices.getUserMedia({
					audio: true,
				});
				setIsMicrophoneAccessible(true);
				microphoneStream.getTracks().forEach((track) => track.stop());
			} catch (error) {
				setIsMicrophoneAccessible(false);
			}
		};

		checkMediaAccess();
	}, []);

	const fetchQuestions = async () => {
		setLoading(true);
		if (token) {
			try {
				const resp = await getAssessmentSheet(token);

				if (resp.status === 200) {
					setLoading(false);
					sessionStorage.setItem("question", JSON.stringify(resp.data));
					navigate("/instruction");
				}
			} catch (error: any) {
				setLoading(false);
				toast.error({
					type: "background",
					duration: 3000,
					status: "Error",
					title: "Error",
					description:
						error.response?.data?.error || error.response?.data?.message || "Failed to load assessment.",
					open: true,
				});
			}
		}
	};

	const takeSelfie = () => {
		if (videoRef.current && canvasRef.current) {
			const video = videoRef.current;
			const canvas = canvasRef.current;
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			const ctx = canvas.getContext("2d");
			ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
			const photoData = canvas.toDataURL("image/png");
			localStorage.setItem("userSelfie", photoData);
			setUserPhoto(photoData);
			setHasTakenSelfie(true);
			const blob = dataURLToBlob(photoData);
			const file = new File([blob], "selfie.png", { type: "image/png" });
			uploadSelfie(file);
		}
	};

	const dataURLToBlob = (dataURL: any) => {
		const byteString = atob(dataURL.split(",")[1]);
		const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
		const ab = new ArrayBuffer(byteString.length);
		const ia = new Uint8Array(ab);
		for (let i = 0; i < byteString.length; i++) {
			ia[i] = byteString.charCodeAt(i);
		}
		return new Blob([ab], { type: mimeString });
	};

	const uploadSelfie = async (imageFile: File) => {
		const formData = new FormData();
		formData.append("image", imageFile);

		try {
			setLoading(true);
			const response = await api.post(
				`/upload_screenshot?token=${token}`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);

			if (response.status === 200) {
				setLoading(false);
			} else {
				setLoading(false);
				alert("Image upload failed,try again");
			}
		} catch (error) {
			setLoading(false);
			alert("Error uploading image , try again");
		}
	};

	const retakeSelfie = () => {
		setUserPhoto(null);
		setHasTakenSelfie(false);
	};

	return (
		<div className="flex-col justify-between ">
			<div className="min-h-[90vh]">
				<AssessmentSideBar />
				<h1 className="text-center font-semibold text-2xl mt-10">
					Welcome To Online Proctored Exam
				</h1>
				<h2 className="text-center font-semibold text-gray-500   text-lg mb-3">
					System Check and Verification Photo
				</h2>
				<div className="w-[90%] mx-auto   ">
					<div className="flex items-center justify-center">
						<div className=" flex-1 mt-4">
							<div className="  rounded-md bg-white w-[650px]  h-[380px] m-auto flex justify-center items-center relative">
								<video
									ref={videoRef}
									className=" shadow-2xl absolute inset-0 w-full h-full object-cover rounded-lg "
									autoPlay
									playsInline
								></video>
								{userPhoto && (
									<img
										src={userPhoto}
										alt="Selfie"
										className="border-2 border-[#009616be] absolute inset-0 w-full h-full object-cover  shadow-2xl rounded-md"
									/>
								)}
							</div>
							<div className="my-4">
								{!hasTakenSelfie && (
									<p className=" text-red-600 text-center my-2">
										* Cannot proceed without a picture
									</p>
								)}
								<p className=" text-gray-500 text-center ">
									Please ensure your face is within this box and there is
									adequate lighting
								</p>
							</div>
							{hasTakenSelfie ? (
								<button
									style={{ width: "30%" }}
									onClick={retakeSelfie}
									className="block mx-auto bg-[#EB9200] hover:bg-[#eb9100d1] text-white py-2 px-4 rounded mt-4 w-full"
								>
									Retake
								</button>
							) : (
								<button
									style={{ width: "30%" }}
									onClick={takeSelfie}
									className="block mx-auto bg-[#BDE4C3] hover:scale-110 transition-all  text-[#438437] py-2 px-4 rounded-lg mt-4 w-full"
								>
									Take a Picture
								</button>
							)}
						</div>
						<div className="flex-1">
							<div
								className={`${isCameraAccessible
									? "border-1 border-[#B3E0BA]"
									: "border-1 border-[#FDC5C5] "
									} flex items-center justify-between mx-auto mb-7  rounded-lg w-[80%] p-2`}
							>
								<div className=" ">
									<div className="flex items-center ">
										<svg
											width="34"
											height="34"
											viewBox="0 0 34 34"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M26.5199 25.84H7.47994C6.32394 25.84 5.43994 24.956 5.43994 23.8V11.56C5.43994 10.404 6.32394 9.52 7.47994 9.52H11.5599C11.6959 9.52 11.8999 9.384 11.9679 9.316L12.7159 7.82C12.9879 7.276 13.6679 6.868 14.2799 6.868H19.7199C20.3319 6.868 21.0119 7.276 21.2839 7.82L22.0319 9.316C22.0999 9.452 22.3039 9.52 22.4399 9.52H26.5199C27.6759 9.52 28.5599 10.404 28.5599 11.56V23.8C28.5599 24.956 27.6759 25.84 26.5199 25.84ZM7.47994 10.88C7.07194 10.88 6.79994 11.152 6.79994 11.56V23.8C6.79994 24.208 7.07194 24.48 7.47994 24.48H26.5199C26.9279 24.48 27.1999 24.208 27.1999 23.8V11.56C27.1999 11.152 26.9279 10.88 26.5199 10.88H22.4399C21.8279 10.88 21.1479 10.472 20.8759 9.928L20.1279 8.432C20.0599 8.296 19.8559 8.16 19.7199 8.16H14.2799C14.1439 8.16 13.9399 8.296 13.8719 8.364L13.1239 9.86C12.8519 10.472 12.1719 10.88 11.5599 10.88H7.47994Z"
												fill="#1C1C1C"
												stroke="#1C1C1C"
												strokeWidth="0.4"
											/>
											<path
												d="M16.9999 23.12C13.5999 23.12 10.8799 20.4 10.8799 17C10.8799 13.6 13.5999 10.88 16.9999 10.88C20.3999 10.88 23.1199 13.6 23.1199 17C23.1199 20.4 20.3999 23.12 16.9999 23.12ZM16.9999 12.24C14.3479 12.24 12.2399 14.348 12.2399 17C12.2399 19.652 14.3479 21.76 16.9999 21.76C19.6519 21.76 21.7599 19.652 21.7599 17C21.7599 14.348 19.6519 12.24 16.9999 12.24Z"
												fill="#1C1C1C"
												stroke="#1C1C1C"
												strokeWidth="0.4"
											/>
											<path
												d="M23.8001 12.92C24.1757 12.92 24.4801 12.6156 24.4801 12.24C24.4801 11.8644 24.1757 11.56 23.8001 11.56C23.4246 11.56 23.1201 11.8644 23.1201 12.24C23.1201 12.6156 23.4246 12.92 23.8001 12.92Z"
												fill="#1C1C1C"
												stroke="#1C1C1C"
												strokeWidth="0.4"
											/>
											<path
												d="M8.15991 8.16H10.8799V8.84H8.15991V8.16ZM16.9999 14.28V13.6C15.0959 13.6 13.5999 15.096 13.5999 17H14.2799C14.2799 15.504 15.5039 14.28 16.9999 14.28Z"
												fill="#1C1C1C"
												stroke="#1C1C1C"
												strokeWidth="0.4"
											/>
										</svg>
										<div className="ml-3 ">Camera</div>
									</div>
								</div>
								<div
									className={`flex-shrink-0 w-9 h-9 rounded-full p-1 ${isCameraAccessible ? " text-green-500" : " text-red-500"
										}`}
								>
									{isCameraAccessible ? (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									)}
								</div>
							</div>

							<div
								className={`${isFullScreenAccessible
									? "border-1 border-[#B3E0BA]"
									: "border-1 border-[#FDC5C5] "
									} flex items-center justify-between mx-auto mb-7  rounded-lg w-[80%] p-2`}
							>
								<div className=" ">
									<div className="flex items-center ">
										<svg
											width="24"
											height="20"
											viewBox="0 0 24 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M12 0L20.7194 0.241162C21.4646 0.261929 22.1772 0.545797 22.7264 1.04066C23.2755 1.53552 23.6244 2.20816 23.7089 2.93512C24.097 6.26131 24.097 9.62011 23.7089 12.9463C23.6244 13.6733 23.2755 14.3459 22.7264 14.8408C22.1772 15.3356 21.4646 15.6195 20.7194 15.6403L14.0977 15.8238V18.2354H17.9935C18.2319 18.2354 18.4606 18.3284 18.6292 18.4938C18.7978 18.6593 18.8925 18.8837 18.8925 19.1177C18.8925 19.3517 18.7978 19.5761 18.6292 19.7416C18.4606 19.907 18.2319 20 17.9935 20H6.00649C5.76806 20 5.53939 19.907 5.37079 19.7416C5.20219 19.5761 5.10747 19.3517 5.10747 19.1177C5.10747 18.8837 5.20219 18.6593 5.37079 18.4938C5.53939 18.3284 5.76806 18.2354 6.00649 18.2354H9.90227V15.8226L3.28065 15.6391C2.5354 15.6183 1.82279 15.3344 1.27363 14.8396C0.724474 14.3447 0.375611 13.6721 0.291086 12.9451C-0.0970285 9.61893 -0.0970285 6.26013 0.291086 2.93394C0.375611 2.20699 0.724474 1.53434 1.27363 1.03948C1.82279 0.54462 2.5354 0.260752 3.28065 0.239986L12 0ZM12 1.7646L3.33219 2.00576C2.68489 2.02341 2.15147 2.50456 2.07715 3.13511C1.70552 6.32846 1.70552 9.55296 2.07715 12.7463C2.15147 13.3769 2.68489 13.858 3.33219 13.8757L11.9988 14.1168L20.6678 13.8757C21.3151 13.858 21.8485 13.3769 21.9228 12.7463C22.2945 9.55296 22.2945 6.32846 21.9228 3.13511C21.8872 2.83014 21.7406 2.54803 21.5101 2.34058C21.2795 2.13312 20.9805 2.01424 20.6678 2.00576L12 1.7646Z"
												fill="#1C1C1C"
											/>
										</svg>

										<div className="ml-3 ">Full Display Mode</div>
									</div>
								</div>
								{isFullScreenAccessible ? (
									<div
										className={`flex-shrink-0 w-9 h-9 rounded-full p-1 ${isFullScreenAccessible
											? " text-green-500"
											: " text-red-500"
											}`}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</div>
								) : (
									<button
										className="bg-[#BDE4C3] px-2 py-1 rounded-md text-[#438437]"
										onClick={enableFullScreen}
									>
										Allow
									</button>
								)}
							</div>

							<div
								className={`${isMicrophoneAccessible
									? "border-1 border-[#B3E0BA]"
									: "border-1 border-[#FDC5C5] "
									} flex items-center justify-between mx-auto mb-7  rounded-lg w-[80%] p-2`}
							>
								<div className=" ">
									<div className="flex items-center ">
										<svg
											width="18"
											height="25"
											viewBox="0 0 18 25"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M12.4286 4.42857C12.4286 2.53502 10.8935 1 8.99998 1C7.10643 1 5.57141 2.53502 5.57141 4.42857V11.2857C5.57141 13.1793 7.10643 14.7143 8.99998 14.7143C10.8935 14.7143 12.4286 13.1793 12.4286 11.2857V4.42857Z"
												stroke="#1C1C1C"
												strokeWidth="2"
											/>
											<path
												d="M1 10.1431V11.2859C1 13.4076 1.84285 15.4425 3.34315 16.9428C4.84344 18.4431 6.87827 19.2859 9 19.2859M9 19.2859C11.1217 19.2859 13.1566 18.4431 14.6569 16.9428C16.1571 15.4425 17 13.4076 17 11.2859V10.1431M9 19.2859V23.8573M9 23.8573H5.57143M9 23.8573H12.4286"
												stroke="#1C1C1C"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>

										<div className="ml-3 ">Microphone</div>
									</div>
								</div>
								<div
									className={`flex-shrink-0 w-9 h-9 rounded-full p-1 ${isMicrophoneAccessible ? " text-green-500" : " text-red-500"
										}`}
								>
									{isMicrophoneAccessible ? (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									)}
								</div>
							</div>
							<div
								className={`${isBrowserAccessible
									? "border-1 border-[#B3E0BA]"
									: "border-1 border-[#FDC5C5] "
									} flex items-center justify-between mx-auto mb-7  rounded-lg w-[80%] p-2`}
							>
								<div className=" ">
									<div className="flex items-center ">
										<svg
											width="23"
											height="23"
											viewBox="0 0 23 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M5 6C5 5.73478 5.13696 5.48043 5.38076 5.29289C5.62456 5.10536 5.95522 5 6.3 5C6.64478 5 6.97544 5.10536 7.21924 5.29289C7.46304 5.48043 7.6 5.73478 7.6 6C7.6 6.26522 7.46304 6.51957 7.21924 6.70711C6.97544 6.89464 6.64478 7 6.3 7C5.95522 7 5.62456 6.89464 5.38076 6.70711C5.13696 6.51957 5 6.26522 5 6ZM8.9 6C8.9 5.73478 9.03696 5.48043 9.28076 5.29289C9.52456 5.10536 9.85522 5 10.2 5H16.7C17.0448 5 17.3754 5.10536 17.6192 5.29289C17.863 5.48043 18 5.73478 18 6C18 6.26522 17.863 6.51957 17.6192 6.70711C17.3754 6.89464 17.0448 7 16.7 7H10.2C9.85522 7 9.52456 6.89464 9.28076 6.70711C9.03696 6.51957 8.9 6.26522 8.9 6Z"
												fill="#1C1C1C"
											/>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M9.49512 1H13.5049C14.8637 1 15.9236 1 16.7722 1.06918C17.6345 1.13959 18.3349 1.28535 18.9649 1.60529C20.0111 2.13829 20.8617 2.98888 21.3947 4.03512C21.7159 4.66512 21.8604 5.36553 21.9308 6.22776C22 7.07641 22 8.13506 22 9.49512V13.5049C22 14.8637 22 15.9236 21.9308 16.7722C21.8604 17.6345 21.7146 18.3349 21.3947 18.9649C20.8617 20.0111 20.0111 20.8617 18.9649 21.3947C18.3349 21.7159 17.6345 21.8604 16.7722 21.9308C15.9236 22 14.8649 22 13.5049 22H9.49512C8.13629 22 7.07641 22 6.22776 21.9308C5.36553 21.8604 4.66512 21.7146 4.03512 21.3947C2.98888 20.8617 2.13829 20.0111 1.60529 18.9649C1.28412 18.3349 1.13959 17.6345 1.06918 16.7722C1 15.9236 1 14.8649 1 13.5049V9.49512C1 8.13629 1 7.07641 1.06918 6.22776C1.13959 5.36553 1.28535 4.66512 1.60529 4.03512C2.13829 2.98888 2.98888 2.13829 4.03512 1.60529C4.66512 1.28412 5.36553 1.13959 6.22776 1.06918C7.07765 1 8.13506 1 9.49512 1ZM6.32906 2.30076C5.54218 2.365 5.02335 2.48853 4.59594 2.70594C3.78213 3.1205 3.1205 3.78213 2.70594 4.59594C2.48853 5.02335 2.365 5.54218 2.30076 6.32906C2.22665 7.23453 2.23035 8.14865 2.23406 9.05906L2.23529 9.64706H20.7647L20.7659 9.05906C20.7696 8.14865 20.7734 7.23453 20.6992 6.32906C20.635 5.54218 20.5115 5.02335 20.2941 4.59594C19.8795 3.78213 19.2179 3.1205 18.4041 2.70594C17.9766 2.48853 17.4578 2.365 16.6722 2.30076C15.8791 2.23529 14.8699 2.23529 13.4765 2.23529H9.52353C8.13012 2.23529 7.12088 2.23529 6.32906 2.30076ZM2.23529 13.4765V10.8824H20.7647V13.4765C20.7647 14.8699 20.7647 15.8791 20.6992 16.6709C20.635 17.4578 20.5115 17.9766 20.2941 18.4041C19.8795 19.2179 19.2179 19.8795 18.4041 20.2941C17.9766 20.5115 17.4578 20.635 16.6722 20.6992C15.8791 20.7647 14.8699 20.7647 13.4765 20.7647H9.52353C8.13012 20.7647 7.12088 20.7647 6.32906 20.6992C5.54218 20.635 5.02335 20.5115 4.59594 20.2941C3.78213 19.8795 3.1205 19.2179 2.70594 18.4041C2.48853 17.9766 2.365 17.4578 2.30076 16.6722C2.23529 15.8791 2.23529 14.8699 2.23529 13.4765Z"
												fill="#1C1C1C"
												stroke="#1C1C1C"
												strokeWidth="0.8"
											/>
										</svg>

										<div className="ml-3 ">Browser</div>
									</div>
								</div>
								<div
									className={`flex-shrink-0 w-9 h-9 rounded-full p-1 ${isBrowserAccessible ? " text-green-500" : " text-red-500"
										}`}
								>
									{isBrowserAccessible ? (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									)}
								</div>
							</div>
							<div
								className={`${!isPhone
									? "border-1 border-[#B3E0BA]"
									: "border-1 border-[#FDC5C5] "
									} flex items-center justify-between mx-auto mb-7  rounded-lg w-[80%] p-2`}
							>
								<div className=" ">
									<div className="flex items-center ">
										<svg
											width="24"
											height="20"
											viewBox="0 0 24 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M12 0L20.7194 0.241162C21.4646 0.261929 22.1772 0.545797 22.7264 1.04066C23.2755 1.53552 23.6244 2.20816 23.7089 2.93512C24.097 6.26131 24.097 9.62011 23.7089 12.9463C23.6244 13.6733 23.2755 14.3459 22.7264 14.8408C22.1772 15.3356 21.4646 15.6195 20.7194 15.6403L14.0977 15.8238V18.2354H17.9935C18.2319 18.2354 18.4606 18.3284 18.6292 18.4938C18.7978 18.6593 18.8925 18.8837 18.8925 19.1177C18.8925 19.3517 18.7978 19.5761 18.6292 19.7416C18.4606 19.907 18.2319 20 17.9935 20H6.00649C5.76806 20 5.53939 19.907 5.37079 19.7416C5.20219 19.5761 5.10747 19.3517 5.10747 19.1177C5.10747 18.8837 5.20219 18.6593 5.37079 18.4938C5.53939 18.3284 5.76806 18.2354 6.00649 18.2354H9.90227V15.8226L3.28065 15.6391C2.5354 15.6183 1.82279 15.3344 1.27363 14.8396C0.724474 14.3447 0.375611 13.6721 0.291086 12.9451C-0.0970285 9.61893 -0.0970285 6.26013 0.291086 2.93394C0.375611 2.20699 0.724474 1.53434 1.27363 1.03948C1.82279 0.54462 2.5354 0.260752 3.28065 0.239986L12 0ZM12 1.7646L3.33219 2.00576C2.68489 2.02341 2.15147 2.50456 2.07715 3.13511C1.70552 6.32846 1.70552 9.55296 2.07715 12.7463C2.15147 13.3769 2.68489 13.858 3.33219 13.8757L11.9988 14.1168L20.6678 13.8757C21.3151 13.858 21.8485 13.3769 21.9228 12.7463C22.2945 9.55296 22.2945 6.32846 21.9228 3.13511C21.8872 2.83014 21.7406 2.54803 21.5101 2.34058C21.2795 2.13312 20.9805 2.01424 20.6678 2.00576L12 1.7646Z"
												fill="#1C1C1C"
											/>
										</svg>
										<div className="ml-3 ">
											{isPhone ? "Switch to Desktop" : "Desktop"}
										</div>
									</div>
								</div>
								<div
									className={`flex-shrink-0 w-9 h-9 rounded-full p-1 ${isPhone ? " text-red-500" : " text-green-500"
										}`}
								>
									{isPhone ? (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									)}
								</div>
							</div>
							<div className="text-xs text-red-500 ml-2">
								{!isMicrophoneAccessible &&
									"Please speak louder or adjust microphone level"}
							</div>
						</div>
					</div>

					<button
						onClick={() => {
							fetchQuestions();
						}}
						disabled={
							!isBrowserAccessible ||
							!isCameraAccessible ||
							!isMicrophoneAccessible ||
							isPhone ||
							!hasTakenSelfie ||
							!isFullScreenAccessible
						}
						className={`w-[18%] mx-auto block ${!isBrowserAccessible ||
							!isCameraAccessible ||
							!isMicrophoneAccessible ||
							isPhone ||
							!hasTakenSelfie ||
							!isFullScreenAccessible
							? "bg-gray-400 cursor-not-allowed "
							: "bg-black hover:scale-110 transition-all"
							} text-white  py-2 px-4 rounded mt-4 mb-5 w-1/2`}
					>
						Take up the test
					</button>
					<canvas ref={canvasRef} className="hidden"></canvas>
				</div>
			</div>
			<AssessmentPagesFooter />
		</div>
	);
};

export default AssessmentRequirements;
